import React, {useEffect, useMemo, memo} from "react";
import {useSelector, useDispatch} from "react-redux";
import {useHistory, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

//components:
import {SearchResultsCarousel} from "../../widgets";
import {CircularProgress, Fade} from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";

//actions:
import {mediaItemPageActions} from "../../redux/MediaItemPage/mediaItemRedux";
import {getAllMediaItems} from "../../redux_slices/mediaItemsSlice";
import {errorActions} from "../../redux/ErrorHandler/errorHandler";
import {connectStateResults} from "react-instantsearch-core";
import {loadUserParameter} from "../../redux_slices/contextSlice";
import {getLanguageId, loadUserLanguageParameter} from "../../redux_slices/userParametersSlice";

import {sendDataLayer} from "../../../app/util/tagmanager";
import {filterMatchingHits, parseHits} from "../MediaItemPage/MediaDashboardLogic";

const VideosSearchResults = connectStateResults(props => {
    const {searchState, allSearchResults, searching, error} = props;
    const query = useMemo(() => searchState?.query, [searchState?.query]);
    const hits = useMemo(() => allSearchResults?.hits || [], [allSearchResults?.hits]);
    const language = useSelector(state => loadUserParameter(state, "language"));
    const languageId = useSelector(loadUserLanguageParameter);
    const parsedHits = useMemo(() => parseHitsByLanguage(hits));

    const filteredByCurrentTrack = (_hits = []) => {
        const trackVideos = trackInfo?.videos || [];
        let hitsVideosIds = [];
        _hits.forEach(hit => {
            if (hit.videoId && !hitsVideosIds.includes(hit.videoId)) hitsVideosIds.push(hit.videoId);
        });

        if (level_id && trackVideos) {
            let allVideosOfTrackWithMatches = [];
            hitsVideosIds.forEach(id => {
                const fileInfo = trackVideos?.find(v => v.id === id) || {};

                if (fileInfo.visible) {
                    const matches = _hits.filter(hit => hit.videoId === id);
                    allVideosOfTrackWithMatches.push({...fileInfo, matches});
                }
            });

            return allVideosOfTrackWithMatches;
        }
        let allVideosWithMatches = [];

        hitsVideosIds.forEach(id => {
            const fileInfo = allMediaItems?.find(v => v.id === id) || {};

            if (fileInfo.visible) {
                const matches = _hits.filter(hit => hit.videoId === id);
                allVideosWithMatches.push({...fileInfo, matches});
            }
        });

        return allVideosWithMatches;
    };

    let history = useHistory();
    const {level_id} = useParams();
    const {t} = useTranslation();

    const trackInfo = useSelector(state => state.trackPage.trackInfo);

    //redux:
    //actions:
    const dispatch = useDispatch();

    //store:
    const {allMediaItems = []} = useSelector(state => state.mediaItems);
    const errorMessage = useSelector(state => state.error.errorMessage);
    const contextSelector = useSelector(state => state.context);
    const trackName = useSelector(state => state.layout.trackName);

    const startVideos = useMemo(() => parseHits(parsedHits), [parsedHits]);
    const matches = useMemo(() => filterMatchingHits(parsedHits), [parsedHits]);

    const filteredVideos = useMemo(() => {
        return allMediaItems && query ? filteredByCurrentTrack(matches) : filteredByCurrentTrack(startVideos);
    }, [allMediaItems, matches]);

    function parseHitsByLanguage (hits) {
        const results = hits.filter(obj => {
            return obj.language_id === languageId;
        });
        return results;
    }

    useEffect(() => {
        dispatch(getAllMediaItems());
        dispatch(getLanguageId(language))
        return () => dispatch(errorActions.setErrorMessage(""));
    }, []);

    useEffect(() => {
        if (query && filteredVideos) {
            const videosIds = filteredVideos?.map(v => v.id);

            if (level_id) {
                sendDataLayer(
                    {
                        event: "track_search_bar",
                        query,
                        track_id: level_id,
                        track_name: trackName,
                        result: videosIds,
                    },
                    {context: contextSelector}
                );
            } else {
                sendDataLayer(
                    {
                        event: "home_search_bar",
                        query,
                        result: videosIds,
                    },
                    {context: contextSelector}
                );
            }
        }
    }, [filteredVideos]);

    const handleMatchClick = ({id, text, time}) => {
        dispatch(mediaItemPageActions.setVideoTerm({type: "keywords", text, time}));

        history.push(`/media/${id}`);
    };

    if (errorMessage)
        return (
            <div className="col-12 d-flex justify-content-center align-items-center py-10">
                <div className="alert alert-light text-dark w-fit-content">
                    <WarningRoundedIcon color="inherit" className="mr-5" />
                    {errorMessage}
                </div>
            </div>
        );
    return query ? (
        <Fade
            in={!!query}
            mountOnEnter={true}
            unmountOnExit={true}
            timeout={{
                appear: 2000,
                enter: 1000,
                exit: 500,
            }}
        >
            <div className="container mt-24 position-relative" style={{minHeight: "40vh"}}>
                {searching ? (
                    <div className="col-12 d-flex justify-content-center align-items-center position-absolute h-100">
                        <CircularProgress />
                    </div>
                ) : null}
                {error ? (
                    <div className="col-12 d-flex justify-content-center align-items-center z-index-2">
                        <div className="alert alert-light font-size-h5 font-weight-bold z-index-3">
                            <WarningRoundedIcon color="inherit" className="mr-5" />
                            {error.message}
                        </div>
                    </div>
                ) : null}
                <SearchResultsCarousel
                    htmlTagTitle={`${filteredVideos.length} ${t("HEADER.SEARCHBAR.RESULTS")}`}
                    videos={filteredVideos}
                    onMatchClick={handleMatchClick}
                    query={query}
                />
            </div>
        </Fade>
    ) : null;
});

export default VideosSearchResults;
